/**
 * graftDigits — Hand Studio E3c (design: HAND_STUDIO_E3_JOINT_GRAFT.md §5–§6,
 * RATIFIED 2026-07-14). ONE responsibility: turn the E3a {@link PlacedHand}
 * (named digits + voxel-space joints) and the E3b {@link HandFrames} (per-joint
 * anatomical directions) into REAL finger bones in the {@link RigDescriptor} —
 * BBSA-named, mapped, carrying authored bone-local jointFrames — so a Mixamo
 * clip's finger channels retarget ONTO them (E3d) instead of landing in
 * RetargetReport.droppedTracks. This is the ADR-004 inversion made concrete for
 * hands: a fingerless generated rig has no finger bones to MATCH, so E3
 * CONSTRUCTS them (ADR-010 §11 — the graft is a first-class 'grafted' provenance,
 * distinct from tip 'synthesized').
 *
 * THE CORE CONSTRUCTION. For each ARTICULATING bone {digit}{1|2|3} (mcp/pip/dip)
 * the frame gives `worldPrimary` (unit, joint→child) and `worldSecondary` (unit,
 * ⊥ primary — the shared dorsal palm normal). We build the bone's world-bind
 * ROTATION so its LOCAL axes align with that anatomical frame:
 *     X = worldPrimary          local +X → along the bone
 *     Y = worldSecondary        local +Y → dorsal (nail) reference
 *     Z = normalize(X × Y)      local +Z → the flexion/curl axis (right-handed)
 *     worldBind.rotation = quatFromBasis(X, Y, Z)     (columns X | Y | Z)
 * BY CONSTRUCTION rotation⁻¹·worldPrimary = (1,0,0) and rotation⁻¹·worldSecondary
 * = (0,1,0), so the authored jointFrame is the CONSTANT (1,0,0)/(0,1,0): the
 * per-joint, per-specimen geometry lives ENTIRELY in each bone's worldBind (hence
 * localBind, hence the retarget bind prior), and the jointFrame merely NAMES the
 * canonical local axes the reorientation ladder's authored tier reads (reorient.ts,
 * where "local" = worldBind.rotation⁻¹·worldDir). This is the design, not a
 * shortcut — family-keyed so authoring the frames ONCE serves every import
 * (priors.ts). The THUMB is not special: its opposition anatomy is captured in
 * its bones' worldBind (built from the thumb's own primary/secondary); its
 * jointFrames are the same (1,0,0)/(0,1,0).
 *
 * The voxel→world transform is a uniform scale + translation ({@link
 * voxelPointToWorld}): positions map through it, while a voxel-space DIRECTION
 * already IS a world direction (the translation cancels in a difference, the
 * uniform scale drops on normalization) — which is why the E3b frames feed
 * straight in. Positions: joints[k] → voxelPointToWorld. Hierarchy: {digit}1
 * parents to the passed hand rig bone, {digit}{k+1} to {digit}{k}. The tip …4
 * has NO frame (the ontology rejects tip frames) — it INHERITS the DIP's rotation
 * and gets NO jointFrame. localBind is the exact decomposition of the
 * (1,1,1)-scaled worldBind against the parent (the synthesize-tips idiom, extended
 * to divide the parent's world scale out of BOTH position and scale, so every
 * link of the multi-bone chain composes back exactly).
 *
 * Fail-loud (LAWS): empty digits (E3a unresolved / no thumb pin), a hand rig bone
 * the descriptor lacks, a grid with no world embedding, a bad family slug, a name
 * collision, a finger ontologyId the rig ALREADY maps (grafting onto a non-fingerless
 * rig would silently redirect that finger's retarget tracks to the graft bone —
 * bonesByOntologyId is last-write-wins), or a degenerate curl axis all throw
 * {@link GraftError} (every issue collected, never just the first). An INCOMPLETE digit (E3a chain-too-short / a
 * missing frame) is a named `graft-incomplete-digit` marker + SKIP — the complete
 * digits still graft, never a silent half-digit. Outputs are re-gated by
 * parseRigDescriptor; the jointFrames pass parsePriorsBundle +
 * requirePriorsCompatible by construction (unit + ⊥, authored:, no tip frame).
 *
 * Depends on: hands/place-digits (E3a), hands/author-frames (E3b),
 * hands/voxel-grid (voxel→world), ir/rig-descriptor, ir/priors, math/quat+vec3.
 * Depended on by: kernel index; E3d finger retarget (later).
 */
import { KernelError } from '../ir/errors.js';
import {
  parseRigDescriptor,
  type MappingEntry,
  type RigBone,
  type RigDescriptor,
} from '../ir/rig-descriptor.js';
import type { JointFrame } from '../ir/priors.js';
import {
  quatFromBasis,
  quatInvertUnit,
  quatMultiply,
  quatRotateVec3,
  type Quat,
} from '../math/quat.js';
import { vec3Cross, vec3Length, vec3Normalize, vec3Sub } from '../math/vec3.js';
import { voxelPointToWorld, type VoxelGrid } from './voxel-grid.js';
import type { DigitName, PlacedHand } from './place-digits.js';
import type { HandFrames } from './author-frames.js';
import type { EvidenceMarker } from './separation-profile.js';

/**
 * How many joints articulate; the …4 tip gets no frame. Named by COUNT rather
 * than by a list of finger joint names (ADR-023 §6): a thumb's articulating
 * joints are `cmc/mcp/ip`, and a hard-coded `['mcp','pip','dip']` would have
 * gone on matching nothing on a thumb without failing to compile.
 */
const ARTICULATING_COUNT = 3;
/** The canonical bone-local axes every grafted frame NAMES (see the module header). */
const LOCAL_PRIMARY = { x: 1, y: 0, z: 0 } as const;
const LOCAL_SECONDARY = { x: 0, y: 1, z: 0 } as const;
/** Family slug shape (mirrors jointFrameSchema) — reject early, before the frames exist. */
const FAMILY_SLUG = /^[a-z0-9-]+$/;

export interface GraftDigitsOptions {
  /** Platform-generator family the authored jointFrames are keyed under (an open slug). */
  readonly family: string;
  /** The rig's own hand bone name the grafted {digit}1 bones parent to (e.g. 'R_Hand'). */
  readonly handRigBone: string;
}

export interface GraftedDigitEntry {
  readonly name: DigitName;
  /** The four NEW rig-bone names, MCP→tip (…1_graft … …4_graft). */
  readonly bones: readonly string[];
}

export interface GraftDigitsResult {
  /** The descriptor with the finger bones added as REAL bones (re-gated by the parser). */
  readonly rig: RigDescriptor;
  /** Mapping entries for the new bones — provenance 'grafted' (ADR-010 §11). */
  readonly mappingAdditions: readonly MappingEntry[];
  /** Family-keyed authored frames for …1/2/3 (never a tip); the reorientation prior. */
  readonly jointFrames: readonly JointFrame[];
  /** One record per grafted digit — the ImportReport's loud account. */
  readonly grafted: readonly GraftedDigitEntry[];
  /** Pass-through E3a/E3b markers + own `graft-incomplete-digit` notes. */
  readonly markers: readonly EvidenceMarker[];
}

/** A cannot-graft condition — a producer/caller bug, fail-loud (ADR-010 §7). */
export class GraftError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot graft digits (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/** Local bind TRS shape (the RigBone transform), reused for the parent-relative math. */
type Trs = RigBone['worldBind'];

/**
 * Decompose a child's world bind against its parent's so `parent ∘ localBind ==
 * child` exactly (the synthesize-tips idiom, extended to divide the parent's
 * world scale out of BOTH position and scale — a graft is a chain bones deep, so
 * every link must compose back cleanly, not just a leaf tip). Callers own the
 * parent-rotation-unit invariant (IBM-derived and basis-built rotations are unit).
 */
export function localBindFromParent(parentWorld: Trs, childWorld: Trs): Trs {
  const invRot = quatInvertUnit(parentWorld.rotation);
  const offsetLocal = quatRotateVec3(invRot, vec3Sub(childWorld.position, parentWorld.position));
  const ps = parentWorld.scale;
  return {
    position: { x: offsetLocal.x / ps.x, y: offsetLocal.y / ps.y, z: offsetLocal.z / ps.z },
    rotation: quatMultiply(invRot, childWorld.rotation),
    scale: { x: childWorld.scale.x / ps.x, y: childWorld.scale.y / ps.y, z: childWorld.scale.z / ps.z },
  };
}

/**
 * Graft the placed + framed digits onto the rig as real, mapped, frame-authored
 * finger bones. See the module header for the full contract. Returns a {@link
 * GraftDigitsResult}; throws {@link GraftError} (collecting every issue).
 */
export function graftDigits(
  rig: RigDescriptor,
  placed: PlacedHand,
  frames: HandFrames,
  grid: VoxelGrid,
  options: GraftDigitsOptions,
): GraftDigitsResult {
  // --- pre-flight fail-loud: wiring/contract violations, all issues collected ---
  const preflight: string[] = [];
  if (placed.digits.length === 0) {
    preflight.push('no placed digits — E3a returned unresolved (resolve the thumb pin); nothing to graft');
  }
  const handBone = rig.bones.find((b) => b.name === options.handRigBone);
  if (handBone === undefined) {
    preflight.push(`handRigBone "${options.handRigBone}" is not in the rig — cannot parent the grafted digits`);
  }
  if (grid.origin === undefined) {
    preflight.push(
      'grid has no `origin` (world embedding) — it was not built from a real mesh (voxelizeMesh); ' +
        'grafting on a box-fixture grid is a wiring bug (ADR-010 §7)',
    );
  }
  if (!FAMILY_SLUG.test(options.family)) {
    preflight.push(`family "${options.family}" is not a valid slug (^[a-z0-9-]+$) — its jointFrames would never validate`);
  }
  if (placed.side !== frames.side) {
    preflight.push(`E3a side "${placed.side}" ≠ E3b side "${frames.side}" — mismatched hand inputs`);
  }
  if (preflight.length > 0) throw new GraftError(preflight);
  const parentHand = handBone as RigBone;

  const framesByName = new Map(frames.digits.map((d) => [d.name, d]));
  const markers: EvidenceMarker[] = [...placed.markers, ...frames.markers];
  const issues: string[] = [];
  const newBones: RigBone[] = [];
  const newNames = new Set<string>();
  const mappingAdditions: MappingEntry[] = [];
  const jointFrames: JointFrame[] = [];
  const grafted: GraftedDigitEntry[] = [];

  for (const digit of placed.digits) {
    const df = framesByName.get(digit.name);
    const frameByJoint = new Map((df?.frames ?? []).map((f) => [f.joint, f] as const));
    // A digit is graftable only when E3a gave it all four joints AND E3b authored
    // a frame for each of the three articulating joints. A short chain / missing
    // frame is a correctable outcome, not a crash — mark it and skip (never a
    // silent half-digit); the complete digits still graft.
    // Which of THIS digit's articulating joints has no frame — asked with the
    // digit's own joint names, so a thumb looks for cmc/mcp/ip (ADR-023 §6).
    const missingFrames = digit.joints
      .slice(0, ARTICULATING_COUNT)
      .map((j) => j.joint)
      .filter((j) => !frameByJoint.has(j));
    if (df === undefined || digit.joints.length < 4 || missingFrames.length > 0) {
      markers.push({
        code: 'graft-incomplete-digit',
        detail: {
          digit: digit.name,
          joints: digit.joints.length,
          frames: df?.frames.length ?? 0,
          reason:
            df === undefined
              ? 'no authored frames for this digit'
              : digit.joints.length < 4
                ? 'fewer than 4 placed joints (mcp/pip/dip/tip)'
                : `missing frame(s): ${missingFrames.join(',')}`,
        },
      });
      continue;
    }

    // Build the four bones MCP→tip: worldBind.position from the placed joint,
    // worldBind.rotation from the anatomical basis (…1/2/3) or inherited (tip …4).
    const digitBones: RigBone[] = [];
    let parentWorld: Trs = parentHand.worldBind;
    let dipRotation: Quat | null = null;
    let degenerate = false;
    for (let k = 0; k < 4; k += 1) {
      const boneId = digit.boneIds[k]!;
      const worldPos = voxelPointToWorld(grid, digit.joints[k]!.position);
      let worldRot: Quat;
      if (k < 3) {
        const frame = frameByJoint.get(digit.joints[k]!.joint)!;
        const xAxis = vec3Normalize(frame.primary); // unit already (E3b) — normalize is a safe no-op
        const yAxis = vec3Normalize(frame.secondary);
        const zRaw = vec3Cross(xAxis, yAxis);
        if (!(vec3Length(zRaw) > 1e-9)) {
          // E3b guarantees ⊥ unit, so this cannot happen — but never normalize a
          // zero vector (fail-loud over a garbage frame).
          issues.push(`${boneId}: curl axis is degenerate (primary ∥ secondary) — no frame basis formable`);
          degenerate = true;
          break;
        }
        worldRot = quatFromBasis(xAxis, yAxis, vec3Normalize(zRaw));
        if (k === 2) dipRotation = worldRot;
      } else {
        // The tip inherits the DIP's rotation — it has no anatomical frame (the
        // ontology rejects tip frames) and never articulates.
        worldRot = dipRotation!;
      }
      const worldBind: Trs = { position: worldPos, rotation: worldRot, scale: { x: 1, y: 1, z: 1 } };
      digitBones.push({
        name: `${boneId}_graft`,
        parent: k === 0 ? parentHand.name : digitBones[k - 1]!.name,
        localBind: localBindFromParent(parentWorld, worldBind),
        worldBind,
      });
      parentWorld = worldBind;
    }
    if (degenerate) continue;

    // Commit: bones + mapping (all four, the tip is a real mapped bone) +
    // jointFrames (…1/2/3 only — the constant canonical axes).
    for (let k = 0; k < 4; k += 1) {
      const bone = digitBones[k]!;
      const boneId = digit.boneIds[k]!;
      newBones.push(bone);
      newNames.add(bone.name);
      mappingAdditions.push({ rigBone: bone.name, ontologyId: boneId, confidence: 0.9, provenance: 'grafted' });
      if (k < 3) {
        jointFrames.push({
          family: options.family,
          ontologyId: boneId,
          localPrimary: LOCAL_PRIMARY,
          localSecondary: LOCAL_SECONDARY,
          provenance: ['authored:hand-studio'],
        });
      }
    }
    grafted.push({ name: digit.name, bones: digitBones.map((b) => b.name) });
  }

  // --- collision guard (fail-loud regardless): new names vs existing + each other ---
  const existing = new Set(rig.bones.map((b) => b.name));
  for (const name of newNames) {
    if (existing.has(name)) issues.push(`grafted bone name "${name}" collides with an existing rig bone`);
  }
  if (newNames.size !== newBones.length) {
    issues.push(`internal: ${newBones.length} grafted bones but ${newNames.size} unique names — duplicate graft id`);
  }
  // --- ontology-mapping collision (fail-loud): the rig already maps a finger id ---
  // bonesByOntologyId (retarget-clip) is last-write-wins, and our additions append
  // AFTER the rig's own mapping — so an existing mapping for a grafted finger id
  // (e.g. a partial rig with a real skinned finger) would be SILENTLY redirected to
  // the graft bone at retarget. Graft is for a FINGERLESS rig; a pre-existing finger
  // mapping is a caller error (ADR-010 §7). (Codex #41 P2.)
  const existingOntologyIds = new Set(rig.mapping.map((m) => m.ontologyId));
  for (const m of mappingAdditions) {
    if (existingOntologyIds.has(m.ontologyId)) {
      issues.push(
        `ontology id "${m.ontologyId}" is already mapped in the rig — grafting "${m.rigBone}" ` +
          `would silently redirect its retarget tracks to the graft bone`,
      );
    }
  }
  if (issues.length > 0) throw new GraftError(issues);

  // --- re-gate: the kernel validator is the seam — grafted bones are REAL bones ---
  const rigWithGraft = parseRigDescriptor({
    ...rig,
    bones: [...rig.bones, ...newBones],
    mapping: [...rig.mapping, ...mappingAdditions],
  });

  return { rig: rigWithGraft, mappingAdditions, jointFrames, grafted, markers };
}
